import type { TFunction } from 'i18next';

import type { ConsignmentSummary, SrfImportValidationError } from '@/entities/consignments';
import type { ConsignmentFormValues } from '@/widgets/consignment-edit-form';

/**
 * Maps the stable `field` keys of `POST /import-srf`'s `200` preview onto the
 * create form. Legacy RITA raised these very failures from `RequestBean`, so the
 * wording is borrowed from its keys rather than reinvented — only the layout is
 * ours. The backend may add keys, so every lookup has to tolerate a miss.
 */
export const SRF_ERROR_FIELDS: Record<
  string,
  { name: keyof ConsignmentFormValues; labelKey: string; messageKey: string }
> = {
  senderOrganization: {
    name: 'senderOrganization',
    labelKey: 'newcmr.senderorg.label',
    messageKey: 'newcmr.missing.sender.message'
  },
  senderContact: {
    name: 'senderContact',
    labelKey: 'newcmr.sendercontact.label',
    messageKey: 'newcmr.missing.sendercontact.message'
  },
  receiverOrganization: {
    name: 'receiverOrganization',
    labelKey: 'newcmr.receiverorg.label',
    messageKey: 'newcmr.missing.receiver.message'
  },
  receiverContact: {
    name: 'receiverContact',
    labelKey: 'newcmr.receivercontact.label',
    messageKey: 'newcmr.missing.receivercontact.message'
  },
  arrivalLocation: {
    name: 'arrivingTo',
    labelKey: 'newcmr.arrivingAt.label',
    messageKey: 'newcmr.unknown.arrival.site.message'
  },
  destinationLocation: {
    name: 'serviceDestination',
    labelKey: 'request.destination.label',
    messageKey: 'newcmr.unknown.delivery.site.message'
  },
  loadDate: {
    name: 'expectedArrivalDate',
    labelKey: 'newcmr.arrivalDate.label',
    messageKey: 'newcmr.badloaddate.message'
  },
  pickupDate: {
    name: 'onSiteDate',
    labelKey: 'request.onSiteDate.label',
    messageKey: 'newcmr.badarrivaldate.message'
  }
};

export type SrfFormFieldTexts = Partial<Record<keyof ConsignmentFormValues, string>>;

/**
 * Legacy wording for a failure we recognise, the server's own message for one we
 * do not. The server only ever speaks English, so the fallback is a safety net
 * for a key added later, not a translation.
 */
export const srfErrorMessage = ({ field, message }: SrfImportValidationError, t: TFunction): string => {
  const target = SRF_ERROR_FIELDS[field];
  return target ? t(target.messageKey) : message;
};

/**
 * When the backend cannot resolve a value it quotes the SRF's own text at the end
 * of the message: `... does not exist in RITA: "World Food Program"`. Legacy RITA
 * kept that text in the field so the user can see what the file actually said. The
 * contract carries it nowhere else, so it is read back out of the message — the
 * robust fix is a dedicated field on `SrfImportValidationErrorDTO`.
 */
const QUOTED_TAIL = /"([^"]*)"\s*$/;

export const srfUnresolvedValue = ({ message }: SrfImportValidationError): string | undefined =>
  QUOTED_TAIL.exec(message)?.[1].trim() || undefined;

/**
 * The unresolved SRF text keyed by form field, for showing as the control's
 * placeholder. Only the organization selects render these today — they are the
 * only failures the backend quotes a value for.
 */
export const toSrfFormFieldHints = (errors: SrfImportValidationError[]): SrfFormFieldTexts =>
  errors.reduce<SrfFormFieldTexts>((acc, error) => {
    const target = SRF_ERROR_FIELDS[error.field];
    const value = srfUnresolvedValue(error);
    if (target && value) {
      acc[target.name] = value;
    }
    return acc;
  }, {});

/**
 * A preview persists nothing, so a contact the SRF introduced arrives named but
 * without an id — there is no option for the field to select. Legacy RITA kept that
 * name in the field; seeding the search with it lets the user pick the real contact
 * or create it, instead of facing an empty required field.
 */
export const toSrfPreviewHints = (consignment?: ConsignmentSummary): SrfFormFieldTexts => {
  const parties = {
    senderContact: consignment?.senderContact,
    receiverContact: consignment?.receiverContact,
    clearingAgentContact: consignment?.clearingAgent
  };

  return Object.entries(parties).reduce<SrfFormFieldTexts>((acc, [field, party]) => {
    const name = party?.contact?.name?.trim();
    if (name && !party?.contact?.id) {
      acc[field as keyof ConsignmentFormValues] = name;
    }
    return acc;
  }, {});
};

/** Messages keyed by form field, ready for `setError`. Unmapped keys are dropped. */
export const toSrfFormFieldErrors = (errors: SrfImportValidationError[], t: TFunction): SrfFormFieldTexts =>
  errors.reduce<SrfFormFieldTexts>((acc, error) => {
    const target = SRF_ERROR_FIELDS[error.field];
    if (target) {
      acc[target.name] = srfErrorMessage(error, t);
    }
    return acc;
  }, {});
